// @file Upgrades a OneDrive/SharePoint sharing link that was pasted or typed as a plain link
// attachment into the same credentialed preview the OneDrive picker produces. A connected user
// whose token can read the file is upgraded silently. Someone who is not connected keeps the plain
// link, and a callout under its preview offers to connect: Connect goes straight to Microsoft's
// sign-in, and a successful connection upgrades that draft's link. Nothing interrupts the paste
// itself. A token that cannot read the file keeps the plain link, and if the person connected from
// the callout, the callout turns into an explanation.
//
// The picker store and the Graph client load with the upgrade job, after the pending mark below.
// A static import from this module would put those chunks on the board boot path.
import { ContentPickerSource } from '@@/bits/content_picker'
import { getCurrentUser } from '@@/bits/current_user'
import device from '@@/bits/device'
import { importWithRetry } from '@@/bits/dynamic_import'
import { captureFetchException } from '@@/bits/error_tracker'
import { isMicrosoftOneDriveUrl } from '@@/bits/microsoft_onedrive_url'
import { isRegistered } from '@@/bits/user_model'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { clearPendingUpgrade, markUpgradePending } from '@@/vuecomposables/microsoft_onedrive_paste_upgrade_pending'

function canUpgradeMicrosoftOneDriveLinks(): boolean {
  return (
    !device.app &&
    isRegistered(getCurrentUser()) &&
    useSurfacePermissionsStore().amIRegistered &&
    useSurfacePostPropertiesStore().isContentPickerSourceEnabled(ContentPickerSource.MicrosoftOneDrive)
  )
}

/**
 * Call right after `url` has been written onto the active draft as a plain link attachment.
 * Resolves asynchronously; the draft is only touched if it still carries that same link.
 */
function offerMicrosoftOneDriveUpgradeForActiveDraft(url: string): void {
  if (!isMicrosoftOneDriveUrl(url)) return
  if (!canUpgradeMicrosoftOneDriveLinks()) return

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const draftCid = surfaceDraftsStore.activeDraftCid
  if (draftCid == null) return

  // The draft cid and the pending mark are fixed before the job chunk loads. Reading the active
  // draft after that import can upgrade a draft the user has since moved to, and waiting to mark
  // lets the anonymous sign-in preview flash.
  markUpgradePending(draftCid, url)

  void importWithRetry(() => import('@@/vuecomposables/microsoft_onedrive_paste_upgrade_job'))
    .then(({ continueMicrosoftOneDriveDraftUpgrade }) => {
      continueMicrosoftOneDriveDraftUpgrade({ draftCid, url })
    })
    .catch((error: unknown) => {
      captureFetchException(error, { source: 'microsoftOneDrivePasteUpgrade', outcome: 'silently_lost' })
      clearPendingUpgrade(draftCid, url)
    })
}

export { offerMicrosoftOneDriveUpgradeForActiveDraft }
